// A PARTNER-FELÜLET FIÓK-SCOPE-ja — a sidebar fiók-váltójának állapota, EGY helyen.
//
// MIÉRT KÜLÖN HOOK (és miért nem a `PortalProvider` belső state-je): a scope a `/portal/me` KÉRÉS
// PARAMÉTERE (a nav-jelvények is rá szűkülnek), a provider viszont MÁR a `me` válaszát kapja. Ha a scope a
// provideren belül élne, körkörös lenne: a keret-adat validálná a scope-ot, a scope viszont szűkítené a
// keret-adatot. Ezért a scope a shell-fetch FÖLÖTT dől el, tiszta kliens-állapotból.
//
// A `null` JELENTÉSE: „Minden fiók" (nincs szűkítés) — és ez az ALAPÉRTELMEZÉS. Ez tudatos termék-döntés:
// a rejtett szűrő a legrosszabb kimenet (a felhasználó 3 számlát lát 30 helyett, és nem hibát érzékel,
// hanem hiányzó adatot). Aki fókuszálni akar, az szándékosan váltson.
import { useCallback, useState } from 'react';

/** A munkamenet-szintű tároló kulcsa. */
const SCOPE_KEY = 'atlas.portal.account';

/**
 * PERZISZTENCIA: `sessionStorage`, NEM `localStorage` — szándékosan.
 *
 * Az újratöltés és a lapváltás megtartja a választást (ez kell), de egy KÉSŐBBI munkamenet mindig
 * „Minden fiók"-kal indul. Enélkül a partner egy hónap múlva visszatérve egy elfelejtett szűrőben ülne, és
 * a hiányzó számlákat adat-hibának hinné. _(A korábbi implementáció `localStorage`-ot használt ÉS az első
 * fiókra esett vissza — a kettő együtt épp a rejtett-szűrő hibaosztályt adta.)_
 */
function readStored(): string | null {
  try {
    const v = window.sessionStorage.getItem(SCOPE_KEY);
    return v === null || v === '' ? null : v;
  } catch {
    // Privát mód / letiltott tároló: a scope csak a lap-életre él (nem hiba).
    return null;
  }
}

export interface PortalScope {
  /** A kiválasztott fiók, VAGY `null` = „Minden fiók". */
  accountId: string | null;
  /** `null` → „Minden fiók" (a tárolt érték törlődik). */
  setAccountId: (id: string | null) => void;
}

export function usePortalScope(): PortalScope {
  const [accountId, setState] = useState<string | null>(readStored);

  const setAccountId = useCallback((id: string | null) => {
    setState(id);
    try {
      if (id === null) window.sessionStorage.removeItem(SCOPE_KEY);
      else window.sessionStorage.setItem(SCOPE_KEY, id);
      // A RÉGI, munkameneteken át élő kulcs takarítása: ha maradt `localStorage`-bejegyzés egy korábbi
      // verzióból, az többé nem hat — de ne üldögéljön ott örökre a böngészőben.
      window.localStorage.removeItem(SCOPE_KEY);
    } catch {
      // lásd `readStored` — a választás ilyenkor újratöltés után nem marad meg
    }
  }, []);

  return { accountId, setAccountId };
}
